import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { createUser, updateUser } from '../../services/userService';
import { getTenants } from '../../services/tenantService';

const UserForm = ({ user, onSubmit, onCancel }) => {
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    role: 'user',
    tenant_id: ''
  });
  const [tenants, setTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load tenants on component mount
  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const response = await getTenants();
        setTenants(response.data || []);
      } catch (err) {
        console.error('Error fetching tenants:', err);
        setTenants([]);
      } finally {
        setLoadingTenants(false);
      }
    };
    fetchTenants();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        username: user.username || '',
        email: user.email || '',
        password: '',
        role: user.role || 'user',
        tenant_id: user.tenant_id || ''
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name || !formData.username || !formData.email) {
      setError('Please fill in all required fields');
      return false;
    }
    if (!user && !formData.password) {
      setError('Password is required for new users');
      return false;
    }
    if (formData.password && formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);

      const userData = { ...formData };
      if (!userData.password) {
        delete userData.password;
      }

      if (user) {
        await updateUser(user.id, userData);
      } else {
        await createUser(userData);
      }

      onSubmit();
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
      console.error('Error saving user:', err);
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { value: 'user', label: 'User' },
    { value: 'qa-user', label: 'QA User' },
    { value: 'qa-manager', label: 'QA Manager' },
    { value: 'admin', label: 'Admin' }
  ];

  if (currentUser.role === 'superadmin') {
    roles.push({ value: 'superadmin', label: 'Super Admin' });
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required={!user}
            fullWidth
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            helperText={user ? "Leave blank to keep current password" : ""}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              name="role"
              value={formData.role}
              onChange={handleChange}
              label="Role"
            >
              {roles.map((role) => (
                <MenuItem
                  key={role.value}
                  value={role.value}
                  disabled={
                    currentUser.role === 'admin' && role.value === 'superadmin'
                  }
                >
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Tenant</InputLabel>
            <Select
              name="tenant_id"
              value={formData.tenant_id}
              onChange={handleChange}
              label="Tenant"
              disabled={loadingTenants}
            >
              <MenuItem value="">
                {loadingTenants ? <CircularProgress size={20} /> : 'None'}
              </MenuItem>
              {tenants.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? 'Saving...' : user ? 'Update User' : 'Create User'}
        </Button>
      </Box>
    </Box>
  );
};

export default UserForm; 